import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SidebarLayout from '../components/SidebarLayout';
import API from '../utils/api';
import { externalLinkProps } from '../utils/url';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  ClipboardList, CheckCircle2, Clock, AlertTriangle, Plus, X, Edit2,
  Users, BarChart3, Trophy, Calendar, ExternalLink, Upload
} from 'lucide-react';
import BigClock from '../components/BigClock';
import TimesheetRosterTab from '../components/TimesheetRosterTab';
import TimesheetUploadDialog from '../components/TimesheetUploadDialog';
import MultiAssigneePicker from '../components/MultiAssigneePicker';
import EmployeePerformanceTab from '../components/EmployeePerformanceTab';
import EmployeeGroupedTaskView from '../components/EmployeeGroupedTaskView';
import DailyPostingTab from '../components/DailyPostingTab';

const EXEC_DEPARTMENTS = ['Social & Content'];

const STATUS_COLORS = {
  'Pending': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  'Completed': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};
const PRIORITY_COLORS = {
  'High': 'bg-rose-500/10 text-rose-400',
  'Medium': 'bg-amber-500/10 text-amber-400',
  'Low': 'bg-slate-500/10 text-slate-400',
};

// ========== KPI ==========
function KPI({ icon, label, value, color }) {
  return (
    <div className={`glass-card p-4 flex items-start justify-between border-${color}-500/10`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div>
        <p className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-semibold mb-1">{label}</p>
        <p className={`text-3xl font-heading font-black text-${color}-400`}>{value}</p>
      </div>
      <div className="opacity-30">{icon}</div>
    </div>
  );
}

// ========== MAIN ==========
export default function ExecutionBoardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const execLevel = isAdmin ? 'edit' : (user?.effective_permissions?.execution || 'none');
  const isExecTeam = execLevel === 'edit' && user?.department === 'Social & Content';
  const canAllocate = ['task_only', 'edit'].includes(execLevel);
  const canExecute = execLevel === 'edit';
  const isFullView = !isExecTeam;
  const canWrite = canAllocate;

  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tab, setTab] = useState('tasks');

  // Filters
  const [fClient, setFClient] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fEmployee, setFEmployee] = useState('');
  const [fType, setFType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [analyticsPeriod, setAnalyticsPeriod] = useState('daily');
  const [aCustomStart, setACustomStart] = useState('');
  const [aCustomEnd, setACustomEnd] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fClient && fClient !== 'all') params.client_id = fClient;
      if (fStatus && fStatus !== 'all') params.status = fStatus;
      if (fEmployee && fEmployee !== 'all') params.assigned_to = fEmployee;
      if (fType && fType !== 'all') params.task_type = fType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const aParams = { period: analyticsPeriod };
      if (analyticsPeriod === 'custom' && aCustomStart && aCustomEnd) {
        aParams.start_date = aCustomStart; aParams.end_date = aCustomEnd;
      }

      const calls = [
        axios.get(`${API}/execution/tasks`, { withCredentials: true, params }),
        axios.get(`${API}/execution/analytics`, { withCredentials: true, params: aParams }),
      ];
      if (isAdmin || isFullView || isExecTeam || canAllocate) {
        calls.push(axios.get(`${API}/clients`, { withCredentials: true }));
        calls.push(axios.get(`${API}/employees`, { withCredentials: true }));
      }
      const results = await Promise.all(calls);
      setTasks(results[0].data);
      setAnalytics(results[1].data);
      if (isAdmin || isFullView || isExecTeam || canAllocate) {
        setClients(results[2].data);
        setEmployees(results[3].data.filter(e => e.status === 'Active' && EXEC_DEPARTMENTS.includes(e.department)));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [fClient, fStatus, fEmployee, fType, dateFrom, dateTo, isAdmin, isFullView, isExecTeam, canAllocate, analyticsPeriod, aCustomStart, aCustomEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const today = new Date().toISOString().split('T')[0];
  const todayTasks = tasks.filter(t => t.delivery_date === today || (!dateFrom && !dateTo));
  const completed = tasks.filter(t => t.status === 'Completed');
  const overdue = tasks.filter(t => t.status === 'Pending' && t.is_overdue);
  const onTimePct = completed.length > 0
    ? Math.round(completed.filter(t => t.on_time).length / completed.length * 100)
    : 0;

  if (loading && tasks.length === 0) {
    return <SidebarLayout title="Execution Board" subtitle="Social & Content Task Management"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Execution Board" subtitle="Social & Content Task Management">
      {/* KPIs */}
      <div className="kpi-scroll md:grid-cols-4 gap-4 mb-4" data-testid="exec-kpis">
        <KPI icon={<ClipboardList className="text-purple-400" />} label="Today's Tasks" value={tasks.length} color="purple" />
        <KPI icon={<CheckCircle2 className="text-emerald-400" />} label="Completed" value={completed.length} color="emerald" />
        <KPI icon={<Clock className="text-cyan-400" />} label="On Time %" value={`${onTimePct}%`} color="cyan" />
        <KPI icon={<AlertTriangle className="text-amber-400" />} label="Overdue" value={overdue.length} color="amber" />
      </div>

      <BigClock />

      {/* Tabs — view/task/edit all see all tabs. Timesheet log form still gated server-side to dept=edit. */}
      <div className="flex items-center gap-2 mb-6 mt-6 overflow-x-auto">
        {[...['tasks', 'posting', 'timesheet', 'output'], ...(isAdmin ? ['review'] : [])].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-colors whitespace-nowrap ${tab === t ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'}`} data-testid={`tab-${t}`}>
            {t === 'tasks' ? (isFullView ? 'All Tasks' : 'My Tasks') : t === 'posting' ? 'Posting' : t === 'timesheet' ? 'Timesheet' : t === 'review' ? 'Performance Review' : 'Output Stats'}
          </button>
        ))}
        <div className="flex-1" />
        {tab === 'tasks' && user?.employee_id && (
          <Button onClick={() => setShowTimesheet(true)} variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 h-9 text-xs" data-testid="upload-timesheet-btn">
            <Clock size={14} className="mr-1" /> Upload Timesheet
          </Button>
        )}
        {tab === 'tasks' && canAllocate && (
          <>
            <Button onClick={() => setShowCSV(true)} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="exec-csv-btn"><Upload size={14} className="mr-1" /> CSV Upload</Button>
            <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 shadow-[0_0_15px_rgba(79,70,229,0.3)] h-9 text-xs" data-testid="create-exec-task-btn"><Plus size={14} className="mr-1" /> New Task</Button>
          </>
        )}
      </div>

      {tab === 'tasks' && (
        <TasksTab tasks={tasks} canWrite={canWrite} isAdmin={isAdmin} isFullView={isFullView}
          clients={clients} employees={employees} user={user}
          fClient={fClient} setFClient={setFClient} fStatus={fStatus} setFStatus={setFStatus}
          fEmployee={fEmployee} setFEmployee={setFEmployee} fType={fType} setFType={setFType}
          dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
          onSelect={setSelectedTask} />
      )}
      {tab === 'timesheet' && (
        <TimesheetRosterTab board="execution" />
      )}
      {tab === 'posting' && (
        <DailyPostingTab user={user} clients={clients} />
      )}
      {tab === 'review' && (
        <EmployeePerformanceTab board="execution" />
      )}
      {tab === 'output' && (
        <OutputTab analytics={analytics} period={analyticsPeriod} setPeriod={setAnalyticsPeriod}
          customStart={aCustomStart} setCustomStart={setACustomStart}
          customEnd={aCustomEnd} setCustomEnd={setACustomEnd} />
      )}

      {showCreate && <CreateDialog clients={clients} employees={employees} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} />}
      {showCSV && <CSVUploadDialog clients={clients} employees={employees} onClose={() => setShowCSV(false)} onDone={() => { setShowCSV(false); fetchData(); }} />}
      {showTimesheet && <TimesheetUploadDialog board="execution" onClose={() => setShowTimesheet(false)} onSaved={() => {}} />}
      {selectedTask && <TaskDetail task={selectedTask} canWrite={canWrite} canExecute={canExecute} isAdmin={isAdmin} user={user} clients={clients} employees={employees} onClose={() => setSelectedTask(null)} onUpdate={() => { setSelectedTask(null); fetchData(); }} />}
    </SidebarLayout>
  );
}

// ========== TASKS TAB ==========
function TasksTab({ tasks, canWrite, isAdmin, isFullView, clients, employees, user, fClient, setFClient, fStatus, setFStatus, fEmployee, setFEmployee, fType, setFType, dateFrom, setDateFrom, dateTo, setDateTo, onSelect }) {
  const [viewMode, setViewMode] = useState('byEmployee'); // 'byEmployee' | 'list'
  return (
    <div className="animate-fadeIn" data-testid="exec-tasks">
      {isFullView && (
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <div className="inline-flex rounded-lg border border-white/10 bg-black/40 overflow-hidden" data-testid="exec-view-toggle">
            <button onClick={() => setViewMode('byEmployee')} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === 'byEmployee' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="exec-view-by-employee">By Employee</button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="exec-view-list">List</button>
          </div>
          <Select value={fClient} onValueChange={setFClient}>
            <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]"><SelectItem value="all">All Clients</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fEmployee} onValueChange={setFEmployee}>
            <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs"><SelectValue placeholder="Employee" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]"><SelectItem value="all">All</SelectItem>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          {viewMode === 'list' && (
            <>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="w-[130px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="all">All</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Completed">Completed</SelectItem></SelectContent>
              </Select>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger className="w-[180px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs"><SelectValue placeholder="Task Type" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="all">All Types</SelectItem>
                  {["Social Media Calendar", "Moodboard", "Content Writing", "Campaign Brief", "Content Strategy", "Hashtag Research", "Influencer Brief", "Report / Analytics Deck", "Posting", "Others"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5" placeholder="From" />
                <span className="text-slate-500 text-xs">to</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5" placeholder="To" />
              </div>
            </>
          )}
        </div>
      )}

      {viewMode === 'byEmployee' && isFullView ? (
        <EmployeeGroupedTaskView tasks={tasks} onSelect={onSelect} testIdPrefix="exec-task" />
      ) : (
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Client</th><th>Assigned</th><th>Type</th><th>Delivery Date</th><th>Priority</th><th>Status</th><th>Timeliness</th></tr></thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500">No tasks found</td></tr>
              ) : tasks.map(t => (
                <tr key={t.task_id} className="cursor-pointer hover:bg-white/[0.02]" onClick={() => onSelect(t)} data-testid={`exec-task-${t.task_id}`}>
                  <td className="text-white text-sm">{t.client_name}</td>
                  <td className="text-slate-300 text-sm">
                    {t.assignee_names?.length > 0 ? t.assignee_names.join(', ') : t.assigned_name}
                  </td>
                  <td className="text-xs text-slate-400">{t.task_type}</td>
                  <td className="text-xs text-slate-400 font-mono">{t.delivery_date}</td>
                  <td>{t.priority && <span className={`text-[10px] px-2 py-0.5 rounded ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>}</td>
                  <td><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[t.status]}`}>{t.status}</span></td>
                  <td>
                    {t.status === 'Completed' && (
                      <span className={`text-[10px] px-2 py-0.5 rounded ${t.on_time ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                        {t.on_time ? 'On Time' : 'Delayed'}
                      </span>
                    )}
                    {t.status === 'Pending' && t.is_overdue && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400">Overdue</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

// ========== OUTPUT STATS ==========
function OutputTab({ analytics, period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd }) {
  const a = analytics;
  const types = ["Social Media Calendar", "Moodboard", "Content Writing", "Campaign Brief", "Content Strategy", "Hashtag Research", "Influencer Brief", "Report / Analytics Deck", "Posting", "Others"];
  const maxType = a ? Math.max(...types.map(t => a.by_type?.[t]?.total || 0), 1) : 1;

  return (
    <div className="animate-fadeIn space-y-6" data-testid="exec-output">
      <div className="flex items-center gap-2 flex-wrap">
        {['daily', 'weekly', 'monthly', 'custom'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white'}`}>
            {p === 'daily' ? 'Today' : p === 'weekly' ? 'This Week' : p === 'monthly' ? 'This Month' : 'Custom'}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5" />
            <span className="text-slate-500 text-xs">to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5" />
          </div>
        )}
      </div>

      {!a ? <div className="text-center py-16 text-slate-500">Loading...</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Total', value: a.total, color: 'text-white', bg: 'bg-white/5' },
              { label: 'Completed', value: a.completed, color: 'text-emerald-400', bg: 'bg-emerald-500/5' },
              { label: 'Pending', value: a.pending, color: 'text-amber-400', bg: 'bg-amber-500/5' },
              { label: 'On Time %', value: `${a.on_time_pct}%`, color: a.on_time_pct >= 80 ? 'text-emerald-400' : 'text-amber-400', bg: 'bg-white/5' },
              { label: 'Delayed %', value: `${a.delayed_pct}%`, color: a.delayed_pct > 20 ? 'text-rose-400' : 'text-slate-400', bg: 'bg-white/5' },
              { label: 'Overdue', value: a.overdue, color: a.overdue > 0 ? 'text-rose-400' : 'text-slate-400', bg: 'bg-rose-500/5' },
            ].map(k => (
              <div key={k.label} className={`${k.bg} border border-white/5 rounded-xl p-4`}>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{k.label}</div>
                <div className={`text-2xl font-heading font-black ${k.color}`}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Type */}
            <div className="glass-card p-5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4 flex items-center gap-2"><BarChart3 size={14} className="text-purple-400" /> Task Type Distribution</h3>
              <div className="space-y-2">
                {types.filter(t => (a.by_type?.[t]?.total || 0) > 0).map(type => {
                  const d = a.by_type?.[type] || {};
                  const pct = maxType > 0 ? (d.total || 0) / maxType * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400 truncate max-w-[180px]">{type}</span>
                        <span className="text-[10px] text-slate-500">{d.completed || 0}/{d.total || 0} done</span>
                      </div>
                      <div className="h-4 bg-white/5 rounded overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded transition-all duration-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                      </div>
                    </div>
                  );
                })}
                {types.every(t => (a.by_type?.[t]?.total || 0) === 0) && <div className="text-center py-4 text-slate-500 text-xs">No data</div>}
              </div>
            </div>

            {/* By Client */}
            <div className="glass-card p-5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4 flex items-center gap-2"><Users size={14} className="text-cyan-400" /> Client Distribution</h3>
              {a.by_client?.length === 0 ? <div className="text-center py-8 text-slate-500 text-xs">No data</div> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {a.by_client?.map(c => (
                    <div key={c.client_id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div>
                        <div className="text-sm text-white font-medium">{c.name}</div>
                        <div className="text-[10px] text-slate-500">{c.completed}/{c.total} completed</div>
                      </div>
                      <div className="text-sm font-bold text-purple-400">{c.total}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* By Employee */}
          <div className="glass-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 flex items-center gap-2"><Trophy size={14} className="text-amber-400" /> Employee Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead><tr><th>Employee</th><th className="text-center">Total</th><th className="text-center">Completed</th><th className="text-center">On Time</th><th className="text-center">On Time %</th></tr></thead>
                <tbody>
                  {a.by_employee?.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-slate-500">No data</td></tr> : a.by_employee?.map(e => (
                    <tr key={e.employee_id}>
                      <td className="text-white font-medium">{e.name}</td>
                      <td className="text-center text-slate-300">{e.total}</td>
                      <td className="text-center text-emerald-400 font-bold">{e.completed}</td>
                      <td className="text-center text-cyan-400">{e.on_time}</td>
                      <td className="text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${e.on_time_pct >= 80 ? 'bg-emerald-500/10 text-emerald-400' : e.on_time_pct >= 50 ? 'bg-amber-500/10 text-amber-400' : 'bg-rose-500/10 text-rose-400'}`}>{e.on_time_pct}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ========== CREATE / EDIT DIALOG ==========
function CreateDialog({ clients, employees, existingTask, onClose, onCreated }) {
  const isEdit = !!existingTask;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => existingTask ? {
    client_id: existingTask.client_id || '',
    assignee_ids: existingTask.assignee_ids || (existingTask.assigned_to ? [existingTask.assigned_to] : []),
    task_type: existingTask.task_type || '',
    delivery_date: existingTask.delivery_date || '',
    priority: existingTask.priority || '',
    description: existingTask.description || '',
    reference_links: existingTask.reference_links || '',
    notes: existingTask.notes || '',
  } : { client_id: '', assignee_ids: [], task_type: '', delivery_date: '', priority: '', description: '', reference_links: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.priority) delete payload.priority;
      if (!payload.description) delete payload.description;
      if (!payload.reference_links) delete payload.reference_links;
      if (!payload.notes) delete payload.notes;
      if (isEdit) {
        await axios.put(`${API}/execution/tasks/${existingTask.task_id}`, payload, { withCredentials: true });
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/execution/tasks`, payload, { withCredentials: true });
        toast.success('Task created');
      }
      onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">{isEdit ? 'Edit Task' : 'Create Task'}</DialogTitle></DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Client *</label>
            <Select value={form.client_id} onValueChange={v => set('client_id', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="exec-client"><SelectValue placeholder="Select Client" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Assignees * <span className="text-slate-600 normal-case">(one or more)</span></label>
            <MultiAssigneePicker
              employees={employees}
              value={form.assignee_ids}
              onChange={ids => set('assignee_ids', ids)}
              testid="exec-assignees"
              placeholder="Select assignees"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Task Type *</label>
              <Select value={form.task_type} onValueChange={v => set('task_type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="exec-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {["Social Media Calendar", "Moodboard", "Content Writing", "Campaign Brief", "Content Strategy", "Hashtag Research", "Influencer Brief", "Report / Analytics Deck", "Posting", "Others"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Delivery Date *</label>
              <input type="date" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="exec-date" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Priority</label>
            <Select value={form.priority} onValueChange={v => set('priority', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
            </Select>
          </div>
          {form.task_type === 'Others' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Description *</label>
              <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the task..." className="bg-black/40 border-white/10 text-white h-9 text-sm" />
            </div>
          )}
          {form.task_type !== 'Others' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Description</label>
              <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" className="bg-black/40 border-white/10 text-white h-9 text-sm" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Reference Links</label>
            <Input value={form.reference_links} onChange={e => set('reference_links', e.target.value)} placeholder="Reference URL" className="bg-black/40 border-white/10 text-white h-9 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional notes" className="bg-black/40 border-white/10 text-white h-9 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.client_id || form.assignee_ids.length === 0 || !form.task_type || !form.delivery_date || (form.task_type === 'Others' && !form.description)} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="exec-submit">{loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Task' : 'Create Task')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== TASK DETAIL ==========
function TaskDetail({ task, canWrite, canExecute, isAdmin, user, clients, employees, onClose, onUpdate }) {
  const assigneeIds = task.assignee_ids || (task.assigned_to ? [task.assigned_to] : []);
  const assigneeNames = task.assignee_names?.length ? task.assignee_names : (task.assigned_name ? [task.assigned_name] : []);
  const isOwner = isAdmin || assigneeIds.includes(user?.employee_id);
  const [showComplete, setShowComplete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [supportingLinks, setSupportingLinks] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [loading, setLoading] = useState(false);

  const addLink = () => {
    if (newLink.trim()) {
      setSupportingLinks(prev => [...prev, newLink.trim()]);
      setNewLink('');
    }
  };

  const removeLink = (idx) => setSupportingLinks(prev => prev.filter((_, i) => i !== idx));

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/execution/tasks/${task.task_id}/complete`, { supporting_links: supportingLinks }, { withCredentials: true });
      toast.success('Task completed');
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/execution/tasks/${task.task_id}`, { withCredentials: true });
      toast.success('Task deleted');
      onUpdate();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[task.status]}`}>{task.status}</span>
            {task.status === 'Completed' && (
              <span className={`text-[10px] px-2 py-0.5 rounded ${task.on_time ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>{task.on_time ? 'On Time' : 'Delayed'}</span>
            )}
            {task.status === 'Pending' && task.is_overdue && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400">Overdue</span>
            )}
          </div>
          <SheetTitle className="text-white font-heading text-xl">{task.task_type} — {task.client_name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Client</span><span className="text-white">{task.client_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Assigned To</span><span className="text-white text-right">{assigneeNames.join(', ') || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Delivery Date</span><span className="text-white font-mono">{task.delivery_date}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="text-white">{task.task_type}</span></div>
              {task.priority && <div className="flex justify-between"><span className="text-slate-500">Priority</span><span className={`px-2 py-0.5 rounded ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span></div>}
              {task.description && <div className="flex justify-between"><span className="text-slate-500">Description</span><span className="text-white text-right max-w-[200px]">{task.description}</span></div>}
            </div>
          </div>

          {task.reference_links && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Reference</h3>
              <a {...externalLinkProps(task.reference_links)} className="text-cyan-400 hover:text-cyan-300 break-all text-left text-xs underline underline-offset-2 flex items-center gap-1"><ExternalLink size={12} />{task.reference_links}</a>
            </div>
          )}

          {task.notes && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Notes</h3>
              <p className="text-xs text-slate-300">{task.notes}</p>
            </div>
          )}

          {/* Supporting Links (for completed tasks) */}
          {task.supporting_links?.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-2">Supporting Links</h3>
              <div className="space-y-1">
                {task.supporting_links.map((link, i) => (
                  <a key={i} {...externalLinkProps(link)} className="text-cyan-400 hover:text-cyan-300 break-all text-left text-xs underline underline-offset-2 flex items-center gap-1 w-full"><ExternalLink size={12} />{link}</a>
                ))}
              </div>
            </div>
          )}

          {/* Complete Action */}
          {canExecute && isOwner && task.status === 'Pending' && !showComplete && (
            <Button onClick={() => setShowComplete(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="exec-complete-btn"><CheckCircle2 size={14} className="mr-1" /> Complete Task</Button>
          )}

          {showComplete && (
            <div className="glass-card p-4 space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400">Complete Task</h3>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Supporting Links (optional)</label>
                <div className="flex gap-2">
                  <Input value={newLink} onChange={e => setNewLink(e.target.value)} placeholder="https://..." className="bg-black/40 border-white/10 text-white h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }} />
                  <Button onClick={addLink} variant="outline" className="bg-white/5 border-white/10 text-white h-8 text-xs px-3" data-testid="add-link-btn"><Plus size={12} /></Button>
                </div>
                {supportingLinks.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {supportingLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-cyan-400">
                        <span className="flex-1 truncate">{link}</span>
                        <button onClick={() => removeLink(i)} className="text-rose-400 hover:text-rose-300"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button disabled={loading} onClick={handleComplete} className="w-full bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="exec-confirm-complete">
                {loading ? 'Completing...' : 'Confirm Complete'}
              </Button>
            </div>
          )}

          {canExecute && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => setShowEdit(true)} variant="outline" className="bg-indigo-500/10 border-indigo-500/20 text-indigo-300 h-9 text-xs" data-testid="exec-edit"><Edit2 size={14} className="mr-1" /> Edit Task</Button>
              <Button onClick={handleDelete} variant="outline" className="bg-rose-500/10 border-rose-500/20 text-rose-400 h-9 text-xs" data-testid="exec-delete"><X size={14} className="mr-1" /> Delete Task</Button>
            </div>
          )}
        </div>
      </SheetContent>
      {showEdit && <CreateDialog clients={clients} employees={employees} existingTask={task} onClose={() => setShowEdit(false)} onCreated={() => { setShowEdit(false); onUpdate(); }} />}
    </Sheet>
  );
}


// ========== CSV UPLOAD DIALOG ==========
function CSVUploadDialog({ clients, employees, onClose, onDone }) {
  const [step, setStep] = useState('upload');
  const [mappedRows, setMappedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const TASK_TYPES = ["Social Media Calendar", "Moodboard", "Content Writing", "Campaign Brief", "Content Strategy", "Hashtag Research", "Influencer Brief", "Report / Analytics Deck", "Posting", "Others"];
  const PRIORITIES = ["High", "Medium", "Low"];

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const row = {};
      headers.forEach((h, i) => { row[h] = vals[i] || ''; });
      return row;
    });
  };

  const parseXLSX = async (buffer) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.getWorksheet('Tasks') || wb.worksheets[0];
    if (!ws) return [];
    const headers = [];
    ws.getRow(1).eachCell((cell, colNum) => {
      headers[colNum] = String(cell.value || '').trim().toLowerCase().replace(/\s+/g, '_');
    });
    const rows = [];
    ws.eachRow((row, rowNum) => {
      if (rowNum === 1) return;
      const obj = {};
      let hasData = false;
      row.eachCell((cell, colNum) => {
        const key = headers[colNum];
        if (key) {
          let val = '';
          if (cell.value instanceof Date) {
            const d = cell.value;
            val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else if (typeof cell.value === 'number' && key === 'delivery_date') {
            const d = new Date(Math.round((cell.value - 25569) * 86400 * 1000));
            val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else if (cell.value != null) {
            val = String(cell.value).trim();
            if (key === 'delivery_date' && val.length > 10) {
              const match = val.match(/(\d{4})-(\d{2})-(\d{2})/);
              if (match) val = match[0];
            }
          }
          obj[key] = val;
          if (val) hasData = true;
        }
      });
      if (hasData) rows.push(obj);
    });
    return rows;
  };

  const mapRows = (rows) => {
    return rows.map(row => {
      const clientMatch = clients.find(c => c.name.toLowerCase() === (row.client_name || '').toLowerCase());
      const empMatch = employees.find(e => e.name.toLowerCase() === (row.assigned_to || '').toLowerCase());
      return { ...row, _client_id: clientMatch?.client_id || '', _client_display: clientMatch?.name || '', _emp_id: empMatch?.employee_id || '', _emp_display: empMatch?.name || '', _valid: !!(clientMatch && empMatch) };
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.name.endsWith('.xlsx')) {
      const buffer = await file.arrayBuffer();
      const rows = await parseXLSX(buffer);
      if (rows.length === 0) { toast.error('No data rows found'); return; }
      setMappedRows(mapRows(rows)); setStep('map');
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => { const rows = parseCSV(ev.target.result); if (rows.length === 0) { toast.error('No data'); return; } setMappedRows(mapRows(rows)); setStep('map'); };
      reader.readAsText(file);
    }
  };

  const updateRowMapping = (idx, field, value) => {
    setMappedRows(prev => {
      const updated = [...prev]; const row = { ...updated[idx] };
      if (field === 'client') { const c = clients.find(cl => cl.client_id === value); row._client_id = value; row._client_display = c?.name || ''; }
      else { const emp = employees.find(e => e.employee_id === value); row._emp_id = value; row._emp_display = emp?.name || ''; }
      row._valid = !!(row._client_id && row._emp_id);
      updated[idx] = row; return updated;
    });
  };

  const validRows = mappedRows.filter(r => r._valid);

  const handleUpload = async () => {
    setLoading(true);
    try {
      const rows = validRows.map(r => ({ client_name: r._client_display, assigned_to: r._emp_display, task_type: r.task_type || '', delivery_date: r.delivery_date || '', priority: r.priority || '', description: r.description || '', reference_links: r.reference_links || '', notes: r.notes || '' }));
      const { data } = await axios.post(`${API}/execution/csv-upload`, { rows }, { withCredentials: true });
      setResult(data); setStep('result');
      if (data.created > 0) toast.success(`${data.created} tasks created`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setLoading(false); }
  };

  const downloadExcelTemplate = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Tasks');
    const headers = ['Client Name', 'Assigned To', 'Task Type', 'Delivery Date', 'Priority', 'Description', 'Reference Links', 'Notes'];
    const headerRow = ws.addRow(headers);
    headerRow.eachCell(cell => { cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } }; cell.alignment = { horizontal: 'center' }; });
    ws.columns = [{ width: 20 }, { width: 20 }, { width: 24 }, { width: 14 }, { width: 12 }, { width: 30 }, { width: 30 }, { width: 25 }];

    const clientNames = clients.map(c => c.name);
    const empNames = employees.map(e => e.name);
    const listsSheet = wb.addWorksheet('_Lists'); listsSheet.state = 'veryHidden';
    clientNames.forEach((n, i) => { listsSheet.getCell(`A${i + 1}`).value = n; });
    empNames.forEach((n, i) => { listsSheet.getCell(`B${i + 1}`).value = n; });
    const clientRange = clientNames.length > 0 ? `_Lists!$A$1:$A$${clientNames.length}` : null;
    const empRange = empNames.length > 0 ? `_Lists!$B$1:$B$${empNames.length}` : null;
    const taskTypeList = `"${TASK_TYPES.join(',')}"`;
    const priorityList = `"${PRIORITIES.join(',')}"`;

    for (let r = 2; r <= 101; r++) {
      if (clientRange) ws.getCell(`A${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: [clientRange] };
      if (empRange) ws.getCell(`B${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: [empRange] };
      ws.getCell(`C${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: [taskTypeList] };
      ws.getCell(`D${r}`).numFmt = '@';
      ws.getCell(`E${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: [priorityList] };
    }
    const today = new Date().toISOString().split('T')[0];
    const exRow = ws.getRow(2);
    exRow.getCell(1).value = clientNames[0] || 'Client'; exRow.getCell(2).value = empNames[0] || 'Employee';
    exRow.getCell(3).value = 'Social Media Calendar'; exRow.getCell(4).value = today; exRow.getCell(4).numFmt = '@';
    exRow.getCell(5).value = 'High'; exRow.getCell(8).value = 'Example';
    exRow.eachCell(cell => { cell.font = { italic: true, color: { argb: 'FF888888' } }; });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'execution_board_template.xlsx'; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">Bulk Upload Tasks</DialogTitle></DialogHeader>
        {step === 'upload' && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-slate-400 mb-2 block font-semibold uppercase tracking-widest">Step 1: Download Template</label>
              <p className="text-[11px] text-slate-500 mb-2">Excel template with dropdowns for Client, Employee, Task Type, Priority.</p>
              <Button variant="outline" onClick={downloadExcelTemplate} className="bg-white/5 border-white/10 text-slate-300 text-xs" data-testid="exec-download-template"><Upload size={14} className="mr-1 rotate-180" /> Download Excel Template</Button>
            </div>
            <div className="border-t border-white/5 pt-4">
              <label className="text-xs text-slate-400 mb-2 block font-semibold uppercase tracking-widest">Step 2: Upload</label>
              <input type="file" accept=".csv,.xlsx" onChange={handleFile} ref={fileRef} className="hidden" />
              <Button variant="outline" onClick={() => fileRef.current.click()} className="bg-white/5 border-white/10 text-slate-300 text-xs" data-testid="exec-file-btn"><Upload size={14} className="mr-1" /> Choose File</Button>
            </div>
          </div>
        )}
        {step === 'map' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-400">{mappedRows.length} rows</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{validRows.length} valid</span>
            </div>
            <div className="border border-white/10 rounded overflow-x-auto max-h-[400px]">
              <table className="data-table text-xs w-full">
                <thead><tr><th>#</th><th>Client</th><th>Employee</th><th>Type</th><th>Delivery</th><th>Priority</th><th>OK</th></tr></thead>
                <tbody>
                  {mappedRows.map((row, i) => (
                    <tr key={i} className={row._valid ? '' : 'bg-rose-500/5'}>
                      <td className="text-slate-500">{i + 1}</td>
                      <td>
                        <select value={row._client_id} onChange={(e) => updateRowMapping(i, 'client', e.target.value)} className={`bg-black/40 border rounded px-2 py-1 text-xs w-full ${row._client_id ? 'border-emerald-500/30 text-white' : 'border-rose-500/30 text-rose-400'}`}>
                          <option value="">Select</option>
                          {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={row._emp_id} onChange={(e) => updateRowMapping(i, 'employee', e.target.value)} className={`bg-black/40 border rounded px-2 py-1 text-xs w-full ${row._emp_id ? 'border-emerald-500/30 text-white' : 'border-rose-500/30 text-rose-400'}`}>
                          <option value="">Select</option>
                          {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}
                        </select>
                      </td>
                      <td className="text-slate-300">{row.task_type || '—'}</td>
                      <td className="text-slate-400">{row.delivery_date || '—'}</td>
                      <td className="text-slate-400">{row.priority || '—'}</td>
                      <td className="text-center">{row._valid ? <CheckCircle2 size={14} className="text-emerald-400 mx-auto" /> : <X size={14} className="text-rose-400 mx-auto" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {step === 'result' && result && (
          <div className="space-y-3 py-2">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3 text-xs"><p className="text-emerald-400 font-semibold">Created {result.created} tasks</p></div>
            {result.errors?.length > 0 && (<div className="bg-rose-500/10 border border-rose-500/20 rounded p-3 text-xs space-y-1">{result.errors.map((e, i) => <p key={i} className="text-rose-400">Row {e.row}: {e.error}</p>)}</div>)}
          </div>
        )}
        <DialogFooter>
          {step === 'upload' && <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>}
          {step === 'map' && (<><Button variant="outline" onClick={() => { setStep('upload'); setMappedRows([]); }} className="bg-white/5 border-white/10 text-white">Back</Button><Button disabled={loading || validRows.length === 0} onClick={handleUpload} className="bg-indigo-600 hover:bg-indigo-700" data-testid="exec-csv-confirm">{loading ? 'Uploading...' : `Upload ${validRows.length} Tasks`}</Button></>)}
          {step === 'result' && <Button onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exec-csv-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
